import React, { useEffect, useState, useCallback, forwardRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { deprecate } from 'react-is-deprecated';
import Box from '../Box/index.js';
import Button from '../Button/index.js';

/**
 * DesktopPopover Component
 *
 * Desktop-specific implementation of PopoverSheet that displays content in an elevated container
 * with optional CTA button functionality. The component provides a floating popover experience
 * with smooth animations, flexible positioning, and server-side rendering support.
 *
 * Features:
 * - Animated show/hide transitions with fade and slide effects
 * - Flexible positioning (left, right, center) relative to trigger element
 * - Optional CTA button with customizable label and click handler
 * - Server-side rendering support for SEO optimization
 * - Container element width matching capability
 * - Customizable border colors and styling
 *
 * @component
 * @param {object} props - The component props
 * @param {boolean} props.show - Whether the popover should be visible
 * @param {string} [props.borderColor='transparent'] - Border color of the popover
 * @param {string} props.buttonLabel - Label text for the CTA button
 * @param {React.ReactNode} props.children - Content to display inside the popover
 * @param {string} [props.className] - Additional CSS class names
 * @param {HTMLElement} [props.containerElement] - Container element for width matching
 * @param {boolean} [props.hideCTA] - Whether to hide the CTA button
 * @param {boolean} [props.fullWidth=true] - Whether popover should take full width
 * @param {string} [props.position='left'] - Positioning anchor point
 * @param {function} [props.onClick] - Deprecated: Use onHide instead
 * @param {function} [props.onHide] - Callback when popover is hidden
 * @param {boolean} [props.renderServerSide=false] - Enable server-side rendering
 * @param {object} ref - Forwarded ref to the Box component
 * @returns {JSX.Element|null} The rendered popover component or null if hidden
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const DesktopPopover = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    show,
    borderColor = 'transparent',
    buttonLabel,
    children,
    className,
    containerElement,
    hideCTA,
    fullWidth = true,
    position = 'left',
    onClick,
    // deprecated
    onHide = () => {},
    renderServerSide = false,
    ...rest
  } = _ref;
  const intialShow = renderServerSide || show;
  const [showPopover, setShowPopover] = useState(intialShow); // to show the popover component
  const [openAnimation, setOpenAnimation] = useState(intialShow); // to set the animation
  const [renderedInDom, setRenderedInDom] = useState(renderServerSide); // to render content serverside -- for SEO

  useEffect(() => {
    if (show) {
      setOpenAnimation(true);
      if (renderServerSide) setRenderedInDom(false);
      if (!renderServerSide) setShowPopover(true);
    } else if (!show && !renderedInDom) {
      setOpenAnimation(false);
      if (!renderServerSide) {
        setTimeout(() => {
          setShowPopover(false);
        }, 250); // Need time to show animation before the component is hidden
      }
    }
  }, [show, renderedInDom, renderServerSide]);
  const handleHideClick = useCallback(() => {
    // handle deprecated onClick callback for onHide functionaliuty
    if (typeof onClick === 'function') {
      onClick();
    }
    onHide();
  }, [onClick, onHide]);
  if (!showPopover) {
    return null;
  }
  const popOverWidth = fullWidth ? '100%' : 'initial';
  const style = {
    zIndex: 3,
    // position attaches the component to that edge of the parent element.
    [position]: '0px',
    width: containerElement ? containerElement.offsetWidth + 'px' : popOverWidth
  };
  return /*#__PURE__*/_jsxs(Box, {
    ref: ref,
    "data-cmp": "popoverSheet",
    className: clsx('overlay-shadow margin-top-2 position-absolute', {
      'fade-in-slide-down': openAnimation,
      'fade-out-slide-up': !openAnimation,
      'display-none': renderedInDom
    }, className),
    backgroundColor: "neutral-primary",
    borderColor: borderColor,
    style: style,
    ...rest,
    children: [children, !hideCTA && /*#__PURE__*/_jsx(Button, {
      block: true,
      bsStyle: "primary",
      onClick: handleHideClick,
      className: "margin-top-5",
      children: buttonLabel
    })]
  });
});
export default DesktopPopover;